import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { 
  Shield, LayoutDashboard, FileText, Users, Settings, LogOut, 
  Menu, X, Bell, ChevronDown, Clock, BarChart3, Mail, GitBranch, AlertTriangle, Archive, CalendarClock, Crown, Receipt, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { adminApi } from '@/lib/api';
import NotificationCenter from '@/components/NotificationCenter';
import { PushToggleButton } from '@/components/PushToggleButton';
import { usePageTitle } from '@/hooks/usePageTitle';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, tenant, logout, hasRole } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);

  // Browser tab title: "CogniEtica — Canal de Denúncias — {Empresa}"
  usePageTitle(tenant?.name);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminApi.getDashboard();
        setStats(response.data);
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    };
    fetchStats();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/denuncias', icon: FileText, label: 'Denuncias' },
    { path: '/admin/relatorios', icon: BarChart3, label: 'Relatorios', roles: ['company_admin', 'compliance_officer', 'super_admin'] },
    { path: '/admin/utilizadores', icon: Users, label: 'Utilizadores', roles: ['company_admin', 'super_admin'] },
    { path: '/admin/faturacao', icon: Receipt, label: 'Faturação', roles: ['company_admin', 'compliance_officer', 'dpo', 'super_admin'] },
    { path: '/admin/workflow', icon: GitBranch, label: 'Workflow', roles: ['company_admin', 'super_admin'] },
    { path: '/admin/templates-email', icon: Mail, label: 'Templates Email', roles: ['company_admin', 'super_admin'] },
    { path: '/admin/conflitos', icon: AlertTriangle, label: 'Conflitos Interesse', roles: ['company_admin', 'compliance_officer', 'triage_manager', 'super_admin'] },
    { path: '/admin/retencao', icon: Archive, label: 'Retenção Dados', roles: ['company_admin', 'super_admin'] },
    { path: '/admin/relatorios-agendados', icon: CalendarClock, label: 'Relatórios Agendados', roles: ['company_admin', 'super_admin'] },
    { path: '/admin/definicoes', icon: Settings, label: 'Definicoes', roles: ['company_admin', 'super_admin'] },
  ];

  const filteredNavItems = navItems.filter(item => 
    !item.roles || hasRole(...item.roles)
  );

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const overdueCount = stats ? stats.overdue_acknowledgments + stats.overdue_feedbacks : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 text-white
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo + Tenant name */}
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <Link to="/admin" className="flex items-center gap-2 min-w-0">
              <Shield className="w-8 h-8 shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="font-bold text-sm leading-tight truncate">CogniEtica</span>
                {tenant?.name && (
                  <span className="text-xs text-slate-300 leading-tight truncate">— {tenant.name}</span>
                )}
              </div>
            </Link>
            <button 
              className="lg:hidden p-1 hover:bg-slate-800 rounded"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-3 px-3 py-2 rounded-md transition-colors
                    ${active 
                      ? 'bg-slate-800 text-white' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'}
                  `}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* SLA Alert */}
          {overdueCount > 0 && (
            <div className="mx-4 mb-4 p-3 bg-red-900/50 rounded-lg border border-red-700">
              <div className="flex items-center gap-2 text-red-300">
                <Clock className="w-4 h-4" />
                <span className="text-sm font-medium">Prazos Vencidos</span>
              </div>
              <p className="text-red-100 text-2xl font-bold mt-1">{overdueCount}</p>
            </div>
          )}

          {/* Super Admin Link */}
          {user?.role === 'super_admin' && (
            <div className="mx-4 mb-4">
              <Link 
                to="/super-admin"
                className="flex items-center gap-2 w-full p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition-colors"
              >
                <Crown className="w-5 h-5" />
                <div>
                  <span className="font-medium block">Super Admin</span>
                  <span className="text-xs text-amber-500/70">Gestão de Tenants</span>
                </div>
              </Link>
            </div>
          )}

          {/* User Menu */}
          <div className="p-4 border-t border-slate-700">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-md hover:bg-slate-800 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-slate-400 truncate">{user?.role}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/admin/definicoes')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Definições
                </DropdownMenuItem>
                {user?.is_cogniseg_global && (
                  <DropdownMenuItem
                    onClick={() => navigate('/cogniseg')}
                    className="text-indigo-700"
                    data-testid="switch-cogniseg-tenant-btn"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Trocar de cliente
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 glass-header">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <button 
              className="lg:hidden p-2 hover:bg-slate-100 rounded-md"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1 lg:hidden" />

            <div className="flex items-center gap-4">
              {/* Notifications */}
              <NotificationCenter />

              {/* Web Push toggle (this device) */}
              <PushToggleButton className="text-slate-600 hover:text-slate-900 hover:bg-slate-100" />

              {/* Quick Actions */}
              <Link to={`/denunciar/${tenant?.slug || 'demo'}`} target="_blank" data-testid="view-public-portal-link">
                <Button variant="outline" size="sm">
                  Ver Portal Público
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {tenant?.is_demo && (
          <div
            className="bg-amber-400 text-amber-950 px-4 py-2 border-b border-amber-500 flex items-center justify-center gap-2 text-sm font-semibold tracking-wide"
            data-testid="demo-tenant-banner"
            role="alert"
          >
            <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={2.5} />
            <span className="uppercase">Instância de Teste</span>
            <span className="hidden sm:inline opacity-80 font-normal normal-case">
              — os dados desta instância podem ser apagados a qualquer momento pelo super-admin
            </span>
          </div>
        )}

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
